import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy } from 'react'
import { AppShell } from '@/components/layout/app-shell'
import { RequireAuth, RequireAccess } from '@/components/auth/route-guards'
import { LandingRoute } from '@/components/auth/landing-route'
import { NotFoundPage } from '@/features/misc/not-found-page'
import { PortalShell } from '@/features/customer-portal/portal-shell'

import { PortalOverviewPage } from '@/features/customer-portal/portal-pages'
import { PortalTicketsPage } from '@/features/customer-portal/portal-pages'
import { PortalTicketDetailPage } from '@/features/customer-portal/portal-pages'
import { PortalAssetsPage } from '@/features/customer-portal/portal-pages'
import { PortalAssetDetailPage } from '@/features/customer-portal/portal-pages'
import { PortalKbPage } from '@/features/customer-portal/portal-pages'
import { LoginPage } from '@/features/auth/login-page'
import { SignupWizard } from '@/features/auth/signup-wizard'

// ── Lazy-loaded route components (P62 code-split) ──
const CustomFieldsPage = lazy(() => import('@/features/admin/customization-pages').then((m) => ({ default: m.CustomFieldsPage })))
const WorkflowsPage = lazy(() => import('@/features/admin/customization-pages').then((m) => ({ default: m.WorkflowsPage })))
const CustomReportsPage = lazy(() => import('@/features/admin/customization-pages').then((m) => ({ default: m.CustomReportsPage })))
const FormsPage = lazy(() => import('@/features/admin/customization-pages').then((m) => ({ default: m.FormsPage })))
const DocumentTemplatesPage = lazy(() => import('@/features/admin/customization-pages').then((m) => ({ default: m.DocumentTemplatesPage })))
const NotificationRulesPage = lazy(() => import('@/features/admin/rules-pages').then((m) => ({ default: m.NotificationRulesPage })))
const AlertRulesPage = lazy(() => import('@/features/admin/rules-pages').then((m) => ({ default: m.AlertRulesPage })))
const ContractsPage = lazy(() => import('@/features/admin/rules-pages').then((m) => ({ default: m.ContractsPage })))
const ApiTokensPage = lazy(() => import('@/features/admin/rules-pages').then((m) => ({ default: m.ApiTokensPage })))
const CustomRolesPage = lazy(() => import('@/features/admin/rules-pages').then((m) => ({ default: m.CustomRolesPage })))
const QuotasPage = lazy(() => import('@/features/admin/governance-pages').then((m) => ({ default: m.QuotasPage })))
const WhatsAppTemplatesPage = lazy(() => import('@/features/admin/governance-pages').then((m) => ({ default: m.WhatsAppTemplatesPage })))
const FbrSettingsPage = lazy(() => import('@/features/admin/governance-pages').then((m) => ({ default: m.FbrSettingsPage })))
const AuditCustomizationPage = lazy(() => import('@/features/admin/governance-pages').then((m) => ({ default: m.AuditCustomizationPage })))
const ImportsPage = lazy(() => import('@/features/integrations/integration-pages').then((m) => ({ default: m.ImportsPage })))
const ExportsPage = lazy(() => import('@/features/integrations/integration-pages').then((m) => ({ default: m.ExportsPage })))
const AccountingExportsPage = lazy(() => import('@/features/integrations/integration-pages').then((m) => ({ default: m.AccountingExportsPage })))
const ScheduledReportsPage = lazy(() => import('@/features/integrations/integration-pages').then((m) => ({ default: m.ScheduledReportsPage })))
const SyncAdminPage = lazy(() => import('@/features/integrations/integration-pages').then((m) => ({ default: m.SyncAdminPage })))
const PaymentGatewaysPage = lazy(() => import('@/features/integrations/integration-pages').then((m) => ({ default: m.PaymentGatewaysPage })))
const MarketplaceCatalogPage = lazy(() => import('@/features/marketplace/marketplace-catalog-page').then((m) => ({ default: m.MarketplaceCatalogPage })))
const SellerListingsPage = lazy(() => import('@/features/marketplace/seller-listings-page').then((m) => ({ default: m.SellerListingsPage })))
const MarketplaceOrdersPage = lazy(() => import('@/features/marketplace/marketplace-orders-page').then((m) => ({ default: m.MarketplaceOrdersPage })))
const MarketplaceRecurringPage = lazy(() => import('@/features/marketplace/marketplace-recurring-page').then((m) => ({ default: m.MarketplaceRecurringPage })))
const ComingSoonPage = lazy(() => import('@/features/misc/coming-soon-page').then((m) => ({ default: m.ComingSoonPage })))
const CustomersListPage = lazy(() => import('@/features/customers/customers-list-page').then((m) => ({ default: m.CustomersListPage })))
const CustomerDetailPage = lazy(() => import('@/features/customers/customer-detail-page').then((m) => ({ default: m.CustomerDetailPage })))
const UsersListPage = lazy(() => import('@/features/users/users-list-page').then((m) => ({ default: m.UsersListPage })))
const RolesPage = lazy(() => import('@/features/users/roles-page').then((m) => ({ default: m.RolesPage })))
const SettingsPage = lazy(() => import('@/features/settings/settings-page').then((m) => ({ default: m.SettingsPage })))
const SupportPage = lazy(() => import('@/features/support/support-page').then((m) => ({ default: m.SupportPage })))
const EmployeesListPage = lazy(() => import('@/features/hr/employees-list-page').then((m) => ({ default: m.EmployeesListPage })))
const EmployeeDetailPage = lazy(() => import('@/features/hr/employee-detail-page').then((m) => ({ default: m.EmployeeDetailPage })))
const LeavePage = lazy(() => import('@/features/hr/leave-page').then((m) => ({ default: m.LeavePage })))
const DepartmentsPage = lazy(() => import('@/features/hr/departments-page').then((m) => ({ default: m.DepartmentsPage })))
const PayrollRunsPage = lazy(() => import('@/features/hr/payroll-runs-page').then((m) => ({ default: m.PayrollRunsPage })))
const PayrollRunDetailPage = lazy(() => import('@/features/hr/payroll-run-detail-page').then((m) => ({ default: m.PayrollRunDetailPage })))
const OvertimePage = lazy(() => import('@/features/hr/overtime-page').then((m) => ({ default: m.OvertimePage })))
const LoansPage = lazy(() => import('@/features/hr/loans-page').then((m) => ({ default: m.LoansPage })))
const OnboardingPage = lazy(() => import('@/features/hr/onboarding-page').then((m) => ({ default: m.OnboardingPage })))
const OnboardingDetailPage = lazy(() => import('@/features/hr/onboarding-detail-page').then((m) => ({ default: m.OnboardingDetailPage })))
const SeparationsPage = lazy(() => import('@/features/hr/separations-page').then((m) => ({ default: m.SeparationsPage })))
const SeparationDetailPage = lazy(() => import('@/features/hr/separation-detail-page').then((m) => ({ default: m.SeparationDetailPage })))
const BenefitsPage = lazy(() => import('@/features/hr/benefits-page').then((m) => ({ default: m.BenefitsPage })))
const HelpdeskPage = lazy(() => import('@/features/hr/helpdesk-page').then((m) => ({ default: m.HelpdeskPage })))
const HelpdeskTicketDetailPage = lazy(() => import('@/features/hr/helpdesk-ticket-detail-page').then((m) => ({ default: m.HelpdeskTicketDetailPage })))
const RecruitmentPage = lazy(() => import('@/features/hr/recruitment-page').then((m) => ({ default: m.RecruitmentPage })))
const PerformancePage = lazy(() => import('@/features/hr/performance-page').then((m) => ({ default: m.PerformancePage })))
const TrainingPage = lazy(() => import('@/features/hr/training-page').then((m) => ({ default: m.TrainingPage })))
const DisciplinaryPage = lazy(() => import('@/features/hr/disciplinary-page').then((m) => ({ default: m.DisciplinaryPage })))
const GrievancesPage = lazy(() => import('@/features/hr/grievances-page').then((m) => ({ default: m.GrievancesPage })))
const SuccessionPage = lazy(() => import('@/features/hr/succession-page').then((m) => ({ default: m.SuccessionPage })))
const EssPage = lazy(() => import('@/features/hr/ess-page').then((m) => ({ default: m.EssPage })))
const HrConfigPage = lazy(() => import('@/features/hr-config/hr-config-page').then((m) => ({ default: m.HrConfigPage })))
const ChartOfAccountsPage = lazy(() => import('@/features/accounting/chart-of-accounts-page').then((m) => ({ default: m.ChartOfAccountsPage })))
const JournalEntriesPage = lazy(() => import('@/features/accounting/journal-entries-page').then((m) => ({ default: m.JournalEntriesPage })))
const JournalEntryDetailPage = lazy(() => import('@/features/accounting/journal-entry-detail-page').then((m) => ({ default: m.JournalEntryDetailPage })))
const AccountingPeriodsPage = lazy(() => import('@/features/accounting/accounting-periods-page').then((m) => ({ default: m.AccountingPeriodsPage })))
const FinancialStatementsPage = lazy(() => import('@/features/accounting/financial-statements-page').then((m) => ({ default: m.FinancialStatementsPage })))
const ReconciliationPage = lazy(() => import('@/features/accounting/reconciliation-page').then((m) => ({ default: m.ReconciliationPage })))
const CurrenciesPage = lazy(() => import('@/features/accounting/currencies-page').then((m) => ({ default: m.CurrenciesPage })))
const ReceivablesPage = lazy(() => import('@/features/receivables/receivables-page').then((m) => ({ default: m.ReceivablesPage })))
const SupplierLedgerPage = lazy(() => import('@/features/purchasing/supplier-ledger-page').then((m) => ({ default: m.SupplierLedgerPage })))
const CodReconciliationPage = lazy(() => import('@/features/cod/cod-page').then((m) => ({ default: m.CodReconciliationPage })))
const CodHandoversPage = lazy(() => import('@/features/cod/cod-handovers-page').then((m) => ({ default: m.CodHandoversPage })))
const ExpenseClaimsPage = lazy(() => import('@/features/finance-ops/expense-claims-page').then((m) => ({ default: m.ExpenseClaimsPage })))
const ExpenseClaimDetailPage = lazy(() => import('@/features/finance-ops/expense-claim-detail-page').then((m) => ({ default: m.ExpenseClaimDetailPage })))
const FixedAssetsPage = lazy(() => import('@/features/finance-ops/fixed-assets-page').then((m) => ({ default: m.FixedAssetsPage })))
const FixedAssetDetailPage = lazy(() => import('@/features/finance-ops/fixed-asset-detail-page').then((m) => ({ default: m.FixedAssetDetailPage })))
const BudgetsPage = lazy(() => import('@/features/finance-ops/budgets-page').then((m) => ({ default: m.BudgetsPage })))
const WithholdingTaxPage = lazy(() => import('@/features/finance-ops/withholding-tax-page').then((m) => ({ default: m.WithholdingTaxPage })))
const PosRegisterPage = lazy(() => import('@/features/pos/pos-register-page').then((m) => ({ default: m.PosRegisterPage })))
const RequisitionsPage = lazy(() => import('@/features/procurement/requisitions-page').then((m) => ({ default: m.RequisitionsPage })))
const RequisitionDetailPage = lazy(() => import('@/features/procurement/requisition-detail-page').then((m) => ({ default: m.RequisitionDetailPage })))
const RfqsPage = lazy(() => import('@/features/procurement/rfqs-page').then((m) => ({ default: m.RfqsPage })))
const RfqDetailPage = lazy(() => import('@/features/procurement/rfq-detail-page').then((m) => ({ default: m.RfqDetailPage })))
const OpportunitiesPage = lazy(() => import('@/features/crm/opportunities-page').then((m) => ({ default: m.OpportunitiesPage })))
const SalesQuotationsPage = lazy(() => import('@/features/crm/sales-quotations-page').then((m) => ({ default: m.SalesQuotationsPage })))
const SalesQuotationDetailPage = lazy(() => import('@/features/crm/sales-quotation-detail-page').then((m) => ({ default: m.SalesQuotationDetailPage })))
const GatePassesPage = lazy(() => import('@/features/logistics-ops/gate-passes-page').then((m) => ({ default: m.GatePassesPage })))
const DeliveryChallansPage = lazy(() => import('@/features/logistics-ops/delivery-challans-page').then((m) => ({ default: m.DeliveryChallansPage })))
const BinLocationsPage = lazy(() => import('@/features/logistics-ops/bin-locations-page').then((m) => ({ default: m.BinLocationsPage })))
const WorkCentersPage = lazy(() => import('@/features/manufacturing/work-centers-page').then((m) => ({ default: m.WorkCentersPage })))
const BomsPage = lazy(() => import('@/features/manufacturing/boms-page').then((m) => ({ default: m.BomsPage })))
const WorkOrdersPage = lazy(() => import('@/features/manufacturing/work-orders-page').then((m) => ({ default: m.WorkOrdersPage })))
const MessageThreadsPage = lazy(() => import('@/features/messaging/threads-page').then((m) => ({ default: m.MessageThreadsPage })))
const MessageThreadDetailPage = lazy(() => import('@/features/messaging/thread-detail-page').then((m) => ({ default: m.MessageThreadDetailPage })))
const SlowMovingReportPage = lazy(() => import('@/features/reports/slow-moving-page').then((m) => ({ default: m.SlowMovingReportPage })))
const AiAuditorPage = lazy(() => import('@/features/ai/ai-auditor-page').then((m) => ({ default: m.AiAuditorPage })))
const IntelligencePage = lazy(() => import('@/features/intelligence/intelligence-page').then((m) => ({ default: m.IntelligencePage })))
const AiAnalystPage = lazy(() => import('@/features/ai/ai-analyst-page').then((m) => ({ default: m.AiAnalystPage })))
const AiAssistantPage = lazy(() => import('@/features/ai/ai-assistant-page').then((m) => ({ default: m.AiAssistantPage })))
const GamificationPage = lazy(() => import('@/features/gamification/gamification-page').then((m) => ({ default: m.GamificationPage })))
const LoyaltyPage = lazy(() => import('@/features/gamification/loyalty-page').then((m) => ({ default: m.LoyaltyPage })))
const LeadsListPage = lazy(() => import('@/features/leads/leads-list-page').then((m) => ({ default: m.LeadsListPage })))
const LeadDetailPage = lazy(() => import('@/features/leads/lead-detail-page').then((m) => ({ default: m.LeadDetailPage })))
const ProductsListPage = lazy(() => import('@/features/products/products-list-page').then((m) => ({ default: m.ProductsListPage })))
const ProductDetailPage = lazy(() => import('@/features/products/product-detail-page').then((m) => ({ default: m.ProductDetailPage })))
const CategoriesPage = lazy(() => import('@/features/products/categories-page').then((m) => ({ default: m.CategoriesPage })))
const StockLevelsPage = lazy(() => import('@/features/products/stock-levels-page').then((m) => ({ default: m.StockLevelsPage })))
const PriceListsPage = lazy(() => import('@/features/pricing/price-lists-page').then((m) => ({ default: m.PriceListsPage })))
const PriceListDetailPage = lazy(() => import('@/features/pricing/price-list-detail-page').then((m) => ({ default: m.PriceListDetailPage })))
const SchemesPage = lazy(() => import('@/features/pricing/schemes-page').then((m) => ({ default: m.SchemesPage })))
const RecurringOrdersPage = lazy(() => import('@/features/pricing/recurring-orders-page').then((m) => ({ default: m.RecurringOrdersPage })))
const OrdersListPage = lazy(() => import('@/features/orders/orders-list-page').then((m) => ({ default: m.OrdersListPage })))
const OrderDetailPage = lazy(() => import('@/features/orders/order-detail-page').then((m) => ({ default: m.OrderDetailPage })))
const TargetsPage = lazy(() => import('@/features/targets/targets-page').then((m) => ({ default: m.TargetsPage })))
const CommissionsPage = lazy(() => import('@/features/targets/commissions-page').then((m) => ({ default: m.CommissionsPage })))
const WarehousesPage = lazy(() => import('@/features/stock/warehouses-page').then((m) => ({ default: m.WarehousesPage })))
const StockTransfersPage = lazy(() => import('@/features/stock/stock-transfers-page').then((m) => ({ default: m.StockTransfersPage })))
const StockTransferDetailPage = lazy(() => import('@/features/stock/stock-transfer-detail-page').then((m) => ({ default: m.StockTransferDetailPage })))
const StockAdjustmentsPage = lazy(() => import('@/features/stock/stock-adjustments-page').then((m) => ({ default: m.StockAdjustmentsPage })))
const StockMovementsPage = lazy(() => import('@/features/stock/stock-movements-page').then((m) => ({ default: m.StockMovementsPage })))
const InvoicesListPage = lazy(() => import('@/features/finance/invoices-list-page').then((m) => ({ default: m.InvoicesListPage })))
const InvoiceDetailPage = lazy(() => import('@/features/finance/invoice-detail-page').then((m) => ({ default: m.InvoiceDetailPage })))
const CreditNotesPage = lazy(() => import('@/features/finance/credit-notes-page').then((m) => ({ default: m.CreditNotesPage })))
const DebitNotesPage = lazy(() => import('@/features/finance/debit-notes-page').then((m) => ({ default: m.DebitNotesPage })))
const SalesReturnsPage = lazy(() => import('@/features/finance/sales-returns-page').then((m) => ({ default: m.SalesReturnsPage })))
const PaymentsListPage = lazy(() => import('@/features/payments/payments-list-page').then((m) => ({ default: m.PaymentsListPage })))
const PaymentDetailPage = lazy(() => import('@/features/payments/payment-detail-page').then((m) => ({ default: m.PaymentDetailPage })))
const ReceivablesAgingPage = lazy(() => import('@/features/payments/receivables-aging-page').then((m) => ({ default: m.ReceivablesAgingPage })))
const CustomerStatementPage = lazy(() => import('@/features/payments/customer-statement-page').then((m) => ({ default: m.CustomerStatementPage })))
const BankSettingsPage = lazy(() => import('@/features/payments/bank-settings-page').then((m) => ({ default: m.BankSettingsPage })))
const SuppliersListPage = lazy(() => import('@/features/purchasing/suppliers-list-page').then((m) => ({ default: m.SuppliersListPage })))
const PurchaseOrdersListPage = lazy(() => import('@/features/purchasing/purchase-orders-list-page').then((m) => ({ default: m.PurchaseOrdersListPage })))
const PurchaseOrderDetailPage = lazy(() => import('@/features/purchasing/purchase-order-detail-page').then((m) => ({ default: m.PurchaseOrderDetailPage })))
const GoodsReceiptsPage = lazy(() => import('@/features/purchasing/goods-receipts-page').then((m) => ({ default: m.GoodsReceiptsPage })))
const TerritoriesPage = lazy(() => import('@/features/field-force/territories-page').then((m) => ({ default: m.TerritoriesPage })))
const TerritoryMapPage = lazy(() => import('@/features/field-force/territory-map-page').then((m) => ({ default: m.TerritoryMapPage })))
const TerritoryCoveragePage = lazy(() => import('@/features/field-force/territory-coverage-page').then((m) => ({ default: m.TerritoryCoveragePage })))
const BeatsPage = lazy(() => import('@/features/field-force/beats-page').then((m) => ({ default: m.BeatsPage })))
const SalesRepsListPage = lazy(() => import('@/features/field-force/sales-reps-list-page').then((m) => ({ default: m.SalesRepsListPage })))
const SalesRepDetailPage = lazy(() => import('@/features/field-force/sales-rep-detail-page').then((m) => ({ default: m.SalesRepDetailPage })))
const VisitsListPage = lazy(() => import('@/features/visits/visits-list-page').then((m) => ({ default: m.VisitsListPage })))
const VisitDetailPage = lazy(() => import('@/features/visits/visit-detail-page').then((m) => ({ default: m.VisitDetailPage })))
const CooldownRulesPage = lazy(() => import('@/features/visits/cooldown-rules-page').then((m) => ({ default: m.CooldownRulesPage })))
const VisitOverridesPage = lazy(() => import('@/features/visits/visit-overrides-page').then((m) => ({ default: m.VisitOverridesPage })))
const ServiceTicketsListPage = lazy(() => import('@/features/service/service-tickets-list-page').then((m) => ({ default: m.ServiceTicketsListPage })))
const ServiceTicketDetailPage = lazy(() => import('@/features/service/service-ticket-detail-page').then((m) => ({ default: m.ServiceTicketDetailPage })))
const TechniciansListPage = lazy(() => import('@/features/service/technicians-list-page').then((m) => ({ default: m.TechniciansListPage })))
const PharmaPage = lazy(() => import('@/features/verticals/pharma-page').then((m) => ({ default: m.PharmaPage })))
const PharmaBatchesPage = lazy(() => import('@/features/verticals/pharma-batches-page').then((m) => ({ default: m.PharmaBatchesPage })))
const SamplesPage = lazy(() => import('@/features/samples/samples-page').then((m) => ({ default: m.SamplesPage })))
const ColdChainPage = lazy(() => import('@/features/verticals/cold-chain-page').then((m) => ({ default: m.ColdChainPage })))
const AgriculturePage = lazy(() => import('@/features/verticals/agriculture-page').then((m) => ({ default: m.AgriculturePage })))
const HorecaPage = lazy(() => import('@/features/verticals/horeca-page').then((m) => ({ default: m.HorecaPage })))
const ConstructionPage = lazy(() => import('@/features/verticals/construction-page').then((m) => ({ default: m.ConstructionPage })))

// ─── Batch 60 — 25 sub-feature pages for full module coverage ──────────
const PharmaDrapReportsPage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.PharmaDrapReportsPage })))
const PharmaRxAuditPage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.PharmaRxAuditPage })))
const PharmaPrescriptionsPage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.PharmaPrescriptionsPage })))
const PharmaBrandPerformancePage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.PharmaBrandPerformancePage })))
const ColdChainCustodyPage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.ColdChainCustodyPage })))
const AgricultureToolsPage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.AgricultureToolsPage })))
const HorecaToolsPage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.HorecaToolsPage })))
const AutoPartsPage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.AutoPartsPage })))
const ConstructionToolsPage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.ConstructionToolsPage })))
const ModernTradePage = lazy(() => import('@/features/verticals/vertical-extras-pages').then((m) => ({ default: m.ModernTradePage })))
const ReportsByCategoryPage = lazy(() => import('@/features/misc/production-pages').then((m) => ({ default: m.ReportsByCategoryPage })))
const FbrDashboardPage = lazy(() => import('@/features/misc/production-pages').then((m) => ({ default: m.FbrDashboardPage })))
const SubscriptionActionsPage = lazy(() => import('@/features/misc/production-pages').then((m) => ({ default: m.SubscriptionActionsPage })))
const AuditDeepToolsPage = lazy(() => import('@/features/misc/production-pages').then((m) => ({ default: m.AuditDeepToolsPage })))
const AccountingExportsPageExtended = lazy(() => import('@/features/misc/production-pages').then((m) => ({ default: m.AccountingExportsPage })))
const CommsExtraPage = lazy(() => import('@/features/misc/production-pages').then((m) => ({ default: m.CommsExtraPage })))
const PluginsAdvancedPage = lazy(() => import('@/features/misc/production-pages').then((m) => ({ default: m.PluginsAdvancedPage })))

// Gap-coverage pages (Batch 61 — backups admin, profile, custom dashboards, invoice templates, modules)
const BackupAdminPage = lazy(() => import('@/features/misc/gap-coverage-pages').then((m) => ({ default: m.BackupAdminPage })))
const MyProfilePage = lazy(() => import('@/features/misc/gap-coverage-pages').then((m) => ({ default: m.MyProfilePage })))
const CustomDashboardsPage = lazy(() => import('@/features/misc/gap-coverage-pages').then((m) => ({ default: m.CustomDashboardsPage })))
const InvoiceTemplatesPage = lazy(() => import('@/features/misc/gap-coverage-pages').then((m) => ({ default: m.InvoiceTemplatesPage })))
const PsHoursInvoicesPage = lazy(() => import('@/features/misc/production-pages').then((m) => ({ default: m.PsHoursInvoicesPage })))
const GeofencesPage = lazy(() => import('@/features/misc/production-pages').then((m) => ({ default: m.GeofencesPage })))
const ReportsPage = lazy(() => import('@/features/reports/reports-page').then((m) => ({ default: m.ReportsPage })))
const ControlTowerPage = lazy(() => import('@/features/secondary-sales/control-tower-page').then((m) => ({ default: m.ControlTowerPage })))
const ClaimsPage = lazy(() => import('@/features/claims/claims-page').then((m) => ({ default: m.ClaimsPage })))
const DemandPlanningPage = lazy(() => import('@/features/claims/demand-planning-page').then((m) => ({ default: m.DemandPlanningPage })))
const MerchandisingDashboardPage = lazy(() => import('@/features/merchandising/merchandising-dashboard-page').then((m) => ({ default: m.MerchandisingDashboardPage })))
const MerchandisingSetupPage = lazy(() => import('@/features/merchandising/merchandising-setup-page').then((m) => ({ default: m.MerchandisingSetupPage })))
const VanSalesPage = lazy(() => import('@/features/van-sales/van-sales-page').then((m) => ({ default: m.VanSalesPage })))
const AuditLogsPage = lazy(() => import('@/features/admin/audit-logs-page').then((m) => ({ default: m.AuditLogsPage })))
const NotificationFailuresPage = lazy(() => import('@/features/audit/notification-failures-page').then((m) => ({ default: m.NotificationFailuresPage })))

// ─── Super-admin (DistroOps internal admin) ─────────────────────
const SubscriptionPage = lazy(() => import('@/features/admin/subscription-page').then((m) => ({ default: m.SubscriptionPage })))
const WhatsAppPage = lazy(() => import('@/features/admin/whatsapp-page').then((m) => ({ default: m.WhatsAppPage })))
const WebhooksPage = lazy(() => import('@/features/admin/webhooks-page').then((m) => ({ default: m.WebhooksPage })))
const FbrPage = lazy(() => import('@/features/admin/fbr-page').then((m) => ({ default: m.FbrPage })))
const PluginsPage = lazy(() => import('@/features/admin/plugins-page').then((m) => ({ default: m.PluginsPage })))
const ProfessionalServicesPage = lazy(() => import('@/features/admin/professional-services-page').then((m) => ({ default: m.ProfessionalServicesPage })))
const RecurringOrdersListPage = lazy(() => import('@/features/orders/recurring-orders-list-page').then((m) => ({ default: m.RecurringOrdersListPage })))
const RecurringOrderDetailPage = lazy(() => import('@/features/orders/recurring-order-detail-page').then((m) => ({ default: m.RecurringOrderDetailPage })))
const RefundsListPage = lazy(() => import('@/features/orders/refunds-list-page').then((m) => ({ default: m.RefundsListPage })))
const PurchaseReturnsListPage = lazy(() => import('@/features/orders/purchase-returns-list-page').then((m) => ({ default: m.PurchaseReturnsListPage })))
const ServiceQuotesListPage = lazy(() => import('@/features/orders/service-quotes-list-page').then((m) => ({ default: m.ServiceQuotesListPage })))
const JourneyPlansListPage = lazy(() => import('@/features/field-force/journey-plans-list-page').then((m) => ({ default: m.JourneyPlansListPage })))
const AttendancePage = lazy(() => import('@/features/field-force/attendance-page').then((m) => ({ default: m.AttendancePage })))
const LiveTrackingPage = lazy(() => import('@/features/field-force/live-tracking-page').then((m) => ({ default: m.LiveTrackingPage })))
const CustomerSegmentsPage = lazy(() => import('@/features/customers/customer-segments-page').then((m) => ({ default: m.CustomerSegmentsPage })))
const BrandsPage = lazy(() => import('@/features/products/brands-page').then((m) => ({ default: m.BrandsPage })))
const UnitsOfMeasurePage = lazy(() => import('@/features/products/units-of-measure-page').then((m) => ({ default: m.UnitsOfMeasurePage })))
const PricingRulesListPage = lazy(() => import('@/features/pricing/pricing-rules-list-page').then((m) => ({ default: m.PricingRulesListPage })))
const SchemesListPage = lazy(() => import('@/features/pricing/schemes-list-page').then((m) => ({ default: m.SchemesListPage })))
const InvitationsPage = lazy(() => import('@/features/users/invitations-page').then((m) => ({ default: m.InvitationsPage })))
const SkillsPage = lazy(() => import('@/features/service/skills-page').then((m) => ({ default: m.SkillsPage })))
const SlaPoliciesPage = lazy(() => import('@/features/service/sla-policies-page').then((m) => ({ default: m.SlaPoliciesPage })))
const CustomerAssetsPage = lazy(() => import('@/features/service/customer-assets-page').then((m) => ({ default: m.CustomerAssetsPage })))
const ChecklistTemplatesPage = lazy(() => import('@/features/service/checklist-templates-page').then((m) => ({ default: m.ChecklistTemplatesPage })))
const KnowledgeBasePage = lazy(() => import('@/features/service/knowledge-base-page').then((m) => ({ default: m.KnowledgeBasePage })))
const FieldServiceAnalyticsPage = lazy(() => import('@/features/service/field-service-analytics-page').then((m) => ({ default: m.FieldServiceAnalyticsPage })))
const EdiPage = lazy(() => import('@/features/integrations/edi-page').then((m) => ({ default: m.EdiPage })))
const PortalSessionsPage = lazy(() => import('@/features/customer-portal/portal-sessions-page').then((m) => ({ default: m.PortalSessionsPage })))
const NotificationsPage = lazy(() => import('@/features/polish/polish-pages').then((m) => ({ default: m.NotificationsPage })))
const StorageBrowserPage = lazy(() => import('@/features/polish/polish-pages').then((m) => ({ default: m.StorageBrowserPage })))
const AuditLogDetailPage = lazy(() => import('@/features/polish/polish-pages').then((m) => ({ default: m.AuditLogDetailPage })))

// ─── Logistics ─────────────────────────────────────────────
const DispatchBoardPage = lazy(() =>
  import('@/features/dispatch/components/dispatch-screen').then((m) => ({ default: m.DispatchScreen })),
)
const DeliveryRunsListPage = lazy(() =>
  import('@/features/delivery/delivery-runs-list-page').then((m) => ({ default: m.DeliveryRunsListPage })),
)
const DeliveryRunDetailPage = lazy(() =>
  import('@/features/delivery/delivery-run-detail-page').then((m) => ({ default: m.DeliveryRunDetailPage })),
)
const DriversListPage = lazy(() =>
  import('@/features/delivery/drivers-list-page').then((m) => ({ default: m.DriversListPage })),
)
const DriverPayPage = lazy(() =>
  import('@/features/delivery/driver-pay-page').then((m) => ({ default: m.DriverPayPage })),
)
const DriverCodShiftsPage = lazy(() =>
  import('@/features/delivery/driver-cod-shifts-page').then((m) => ({ default: m.DriverCodShiftsPage })),
)
const PublicTrackingPage = lazy(() =>
  import('@/features/delivery/public-tracking-page').then((m) => ({ default: m.PublicTrackingPage })),
)
const PublicRatingPage = lazy(() =>
  import('@/features/orders/public-rating-page').then((m) => ({ default: m.PublicRatingPage })),
)
const PublicQuotePage = lazy(() =>
  import('@/features/orders/public-quote-page').then((m) => ({ default: m.PublicQuotePage })),
)
const PickListsPage = lazy(() =>
  import('@/features/delivery/pick-lists-page').then((m) => ({ default: m.PickListsPage })),
)
const PickListDetailPage = lazy(() =>
  import('@/features/delivery/pick-list-detail-page').then((m) => ({ default: m.PickListDetailPage })),
)
const RunReplayPage = lazy(() =>
  import('@/features/delivery/run-replay-page').then((m) => ({ default: m.RunReplayPage })),
)
const VehiclesListPage = lazy(() =>
  import('@/features/delivery/vehicles-list-page').then((m) => ({ default: m.VehiclesListPage })),
)
const PalletsPage = lazy(() =>
  import('@/features/warehouse/pallets-page').then((m) => ({ default: m.PalletsPage })),
)
const ShipmentLabelsPage = lazy(() =>
  import('@/features/labels/shipment-labels-page').then((m) => ({ default: m.ShipmentLabelsPage })),
)

// ─── Financial Reconciliation ──────────────────────────────
const FinanceCodHandoversPage = lazy(() =>
  import('@/features/finance/cod-handovers-page').then((m) => ({ default: m.CodHandoversPage })),
)
const BankImportsPage = lazy(() =>
  import('@/features/finance/bank-imports-page').then((m) => ({ default: m.BankImportsPage })),
)

// ─── ZATCA (KSA) ───────────────────────────────────────────
const ZatcaConfigPage = lazy(() =>
  import('@/features/tax/zatca-config-page').then((m) => ({ default: m.ZatcaConfigPage })),
)
const ZatcaEinvoicesPage = lazy(() =>
  import('@/features/tax/zatca-einvoices-page').then((m) => ({ default: m.ZatcaEinvoicesPage })),
)
const ZatcaWebhookEventsPage = lazy(() =>
  import('@/features/tax/zatca-webhook-events-page').then((m) => ({ default: m.ZatcaWebhookEventsPage })),
)

// ─── E-commerce ────────────────────────────────────────────
const EcommerceStoresPage = lazy(() =>
  import('@/features/ecommerce/ecommerce-stores-page').then((m) => ({ default: m.EcommerceStoresPage })),
)

// ─── Notifications ─────────────────────────────────────────
const NotificationsInboxPage = lazy(() =>
  import('@/features/notifications/notifications-inbox-page').then((m) => ({ default: m.NotificationsInboxPage })),
)

/**
 * Route tree. As modules are built batch-by-batch, their real screens
 * replace the ComingSoonPage placeholders. Each protected route is wrapped
 * in RequireAccess with its permission + module so the gate is enforced at
 * the route level, not just hidden in the sidebar.
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/signup',
    element: <SignupWizard />,
  },
  {
    path: '/track/:token',
    element: <PublicTrackingPage />,
  },
  {
    path: '/rate/:token',
    element: <PublicRatingPage />,
  },
  {
    path: '/quote/:token',
    element: <PublicQuotePage />,
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <LandingRoute /> },
          { path: 'support', element: <SupportPage /> },
          {
            element: <RequireAccess permission="hr.view" module="hr_core" />,
            children: [
              { path: 'hr/employees', element: <EmployeesListPage /> },
              { path: 'hr/employees/:id', element: <EmployeeDetailPage /> },
              { path: 'hr/departments', element: <DepartmentsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="leave.manage" module="leave" />,
            children: [
              { path: 'hr/leave', element: <LeavePage /> },
            ],
          },
          {
            element: <RequireAccess permission="payroll.view" module="payroll" />,
            children: [
              { path: 'hr/payroll', element: <PayrollRunsPage /> },
              { path: 'hr/payroll/:id', element: <PayrollRunDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="overtime.view" module="payroll" />,
            children: [
              { path: 'hr/overtime', element: <OvertimePage /> },
            ],
          },
          {
            element: <RequireAccess permission="loans.view" module="payroll" />,
            children: [
              { path: 'hr/loans', element: <LoansPage /> },
            ],
          },
          {
            element: <RequireAccess permission="onboarding.view" module="hr_core" />,
            children: [
              { path: 'hr/onboarding', element: <OnboardingPage /> },
              { path: 'hr/onboarding/:id', element: <OnboardingDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="offboarding.view" module="hr_core" />,
            children: [
              { path: 'hr/separations', element: <SeparationsPage /> },
              { path: 'hr/separations/:id', element: <SeparationDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="benefits.view" module="hr_core" />,
            children: [
              { path: 'hr/benefits', element: <BenefitsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="helpdesk.manage" module="hr_core" />,
            children: [
              { path: 'hr/helpdesk', element: <HelpdeskPage /> },
              { path: 'hr/helpdesk/:id', element: <HelpdeskTicketDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="recruitment.manage" module="recruitment" />,
            children: [
              { path: 'hr/recruitment', element: <RecruitmentPage /> },
            ],
          },
          {
            element: <RequireAccess permission="performance.manage" module="performance" />,
            children: [
              { path: 'hr/performance', element: <PerformancePage /> },
              { path: 'hr/training', element: <TrainingPage /> },
            ],
          },
          {
            element: <RequireAccess permission="disciplinary.view" module="hr_core" />,
            children: [
              { path: 'hr/disciplinary', element: <DisciplinaryPage /> },
            ],
          },
          {
            element: <RequireAccess permission="grievance.manage" module="hr_core" />,
            children: [
              { path: 'hr/grievances', element: <GrievancesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="talent.view" module="performance" />,
            children: [
              { path: 'hr/succession', element: <SuccessionPage /> },
            ],
          },
          {
            element: <RequireAccess permission="ess.access" module="hr_core" />,
            children: [
              { path: 'hr/me', element: <EssPage /> },
            ],
          },
          {
            element: <RequireAccess permission="hr.view" module="hr_core" />,
            children: [
              { path: 'hr/config', element: <HrConfigPage /> },
            ],
          },

          // ── Accounting / GL ──────────────────────────────────
          {
            element: <RequireAccess permission="accounting.view" module="accounting" />,
            children: [
              { path: 'accounting/chart-of-accounts', element: <ChartOfAccountsPage /> },
              { path: 'accounting/journal-entries', element: <JournalEntriesPage /> },
              { path: 'accounting/journal-entries/:id', element: <JournalEntryDetailPage /> },
              { path: 'accounting/periods', element: <AccountingPeriodsPage /> },
              { path: 'accounting/reconciliation', element: <ReconciliationPage /> },
              { path: 'accounting/currencies', element: <CurrenciesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="accounting.statements.view" module="accounting" />,
            children: [
              { path: 'accounting/statements', element: <FinancialStatementsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="payments.view" module="payments" />,
            children: [
              { path: 'accounting/receivables', element: <ReceivablesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="cod_handover.view" module="payments" />,
            children: [
              { path: 'accounting/cod-reconciliation', element: <CodReconciliationPage /> },
              { path: 'accounting/cod-handovers', element: <CodHandoversPage /> },
            ],
          },
          {
            element: <RequireAccess permission="expenses.view" module="expenses" />,
            children: [
              { path: 'accounting/expenses', element: <ExpenseClaimsPage /> },
              { path: 'accounting/expenses/:id', element: <ExpenseClaimDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="fixed_assets.view" module="fixed_assets" />,
            children: [
              { path: 'accounting/fixed-assets', element: <FixedAssetsPage /> },
              { path: 'accounting/fixed-assets/:id', element: <FixedAssetDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="budgeting.view" module="budgeting" />,
            children: [
              { path: 'accounting/budgets', element: <BudgetsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="withholding_tax.view" module="withholding_tax" />,
            children: [
              { path: 'accounting/withholding-tax', element: <WithholdingTaxPage /> },
            ],
          },
          {
            element: <RequireAccess permission="pos.operate" module="pos" />,
            children: [
              { path: 'pos', element: <PosRegisterPage /> },
            ],
          },
          {
            element: <RequireAccess permission="procurement.requisitions.manage" module="procurement_requisitions" />,
            children: [
              { path: 'procurement/requisitions', element: <RequisitionsPage /> },
              { path: 'procurement/requisitions/:id', element: <RequisitionDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="procurement.rfq.manage" module="rfq" />,
            children: [
              { path: 'procurement/rfqs', element: <RfqsPage /> },
              { path: 'procurement/rfqs/:id', element: <RfqDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="suppliers.view" module="purchases" />,
            children: [
              { path: 'procurement/supplier-ledger', element: <SupplierLedgerPage /> },
            ],
          },
          {
            element: <RequireAccess permission="crm.pipeline.manage" module="crm_pipeline" />,
            children: [
              { path: 'crm/opportunities', element: <OpportunitiesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="gate_passes.manage" module="gate_passes" />,
            children: [
              { path: 'logistics/gate-passes', element: <GatePassesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="delivery_challans.manage" module="delivery_challans" />,
            children: [
              { path: 'logistics/delivery-challans', element: <DeliveryChallansPage /> },
            ],
          },
          {
            element: <RequireAccess permission="bin_locations.manage" module="bin_locations" />,
            children: [
              { path: 'warehouses/bin-locations', element: <BinLocationsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="manufacturing.view" module="manufacturing" />,
            children: [
              { path: 'manufacturing/work-centers', element: <WorkCentersPage /> },
              { path: 'manufacturing/boms', element: <BomsPage /> },
              { path: 'manufacturing/work-orders', element: <WorkOrdersPage /> },
            ],
          },
          {
            element: <RequireAccess permission="bin_locations.manage" />,
            children: [
              { path: 'reports/slow-moving', element: <SlowMovingReportPage /> },
            ],
          },
          {
            element: <RequireAccess permission="messaging.use" module="internal_messaging" />,
            children: [
              { path: 'messages', element: <MessageThreadsPage /> },
              { path: 'messages/:id', element: <MessageThreadDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="ai.auditor.view" module="ai_auditor" />,
            children: [
              { path: 'ai/auditor', element: <AiAuditorPage /> },
            ],
          },
          {
            element: <RequireAccess permission="ai.analyst.view" module="ai_analyst" />,
            children: [
              { path: 'ai/analyst', element: <AiAnalystPage /> },
            ],
          },
          {
            element: <RequireAccess permission="ai.assistant.use" module="ai_assistant" />,
            children: [
              { path: 'ai/assistant', element: <AiAssistantPage /> },
            ],
          },
          {
            element: <RequireAccess permission="intelligence.view" module="intelligence" />,
            children: [
              { path: 'intelligence', element: <IntelligencePage /> },
            ],
          },
          {
            element: <RequireAccess permission="gamification.view" module="gamification" />,
            children: [
              { path: 'gamification', element: <GamificationPage /> },
            ],
          },
          {
            element: <RequireAccess permission="loyalty.view" module="loyalty" />,
            children: [
              { path: 'loyalty', element: <LoyaltyPage /> },
            ],
          },

          // ── Sales & CRM ──────────────────────────────────────
          {
            element: <RequireAccess permission="customers.view" module="customers" />,
            children: [
              { path: 'customers', element: <CustomersListPage /> },
              { path: 'customers/:id', element: <CustomerDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="leads.view" module="leads" />,
            children: [
              { path: 'leads', element: <LeadsListPage /> },
              { path: 'leads/:id', element: <LeadDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="orders.view" module="orders" />,
            children: [
              { path: 'orders', element: <OrdersListPage /> },
              { path: 'orders/:id', element: <OrderDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="secondary_sales.view" module="secondary_sales" />,
            children: [
              { path: 'secondary-sales', element: <ControlTowerPage /> },
            ],
          },
          {
            element: <RequireAccess permission="claims.view" module="claims" />,
            children: [
              { path: 'scheme-claims', element: <ClaimsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="forecasting.view" module="forecasting" />,
            children: [
              { path: 'demand-planning', element: <DemandPlanningPage /> },
            ],
          },
          {
            element: <RequireAccess permission="merchandising.view" module="merchandising" />,
            children: [
              { path: 'merchandising', element: <MerchandisingDashboardPage /> },
            ],
          },
          {
            element: <RequireAccess permission="merchandising.manage" module="merchandising" />,
            children: [
              { path: 'merchandising/setup', element: <MerchandisingSetupPage /> },
            ],
          },
          {
            element: <RequireAccess permission="van_sales.view" module="van_sales" />,
            children: [
              { path: 'van-sales', element: <VanSalesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="sales_targets.view" module="sales_targets" />,
            children: [{ path: 'sales-targets', element: <TargetsPage /> }],
          },
          {
            element: <RequireAccess permission="commissions.view" module="commissions" />,
            children: [{ path: 'commissions', element: <CommissionsPage /> }],
          },
          {
            element: <RequireAccess permission="sales.quotes.manage" module="sales_quotes" />,
            children: [
              { path: 'quotations', element: <SalesQuotationsPage /> },
              { path: 'quotations/:id', element: <SalesQuotationDetailPage /> },
            ],
          },

          // ── Catalog & Inventory ──────────────────────────────
          {
            element: <RequireAccess permission="products.view" module="products" />,
            children: [
              { path: 'products', element: <ProductsListPage /> },
              { path: 'products/:id', element: <ProductDetailPage /> },
              { path: 'product-categories', element: <CategoriesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="products.view" module="inventory" />,
            children: [{ path: 'stock', element: <StockLevelsPage /> }],
          },
          {
            element: <RequireAccess permission="warehouses.view" module="warehouses" />,
            children: [{ path: 'warehouses', element: <WarehousesPage /> }],
          },
          {
            element: <RequireAccess permission="stock_transfers.view" module="stock_transfers" />,
            children: [
              { path: 'stock-transfers', element: <StockTransfersPage /> },
              { path: 'stock-transfers/:id', element: <StockTransferDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="stock_adjustments.view" module="stock_management" />,
            children: [{ path: 'stock-adjustments', element: <StockAdjustmentsPage /> }],
          },
          {
            element: <RequireAccess permission="products.view" module="inventory" />,
            children: [{ path: 'stock-movements', element: <StockMovementsPage /> }],
          },
          {
            element: <RequireAccess permission="price_lists.view" module="price_lists" />,
            children: [
              { path: 'price-lists', element: <PriceListsPage /> },
              { path: 'price-lists/:id', element: <PriceListDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="schemes.view" module="schemes" />,
            children: [{ path: 'schemes', element: <SchemesPage /> }],
          },
          {
            element: <RequireAccess permission="orders.view" module="recurring_orders" />,
            children: [{ path: 'recurring-orders', element: <RecurringOrdersPage /> }],
          },

          // ── Finance ──────────────────────────────────────────
          {
            element: <RequireAccess permission="invoices.view" module="invoices" />,
            children: [
              { path: 'invoices', element: <InvoicesListPage /> },
              { path: 'invoices/:id', element: <InvoiceDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="credit_notes.view" module="credit_notes" />,
            children: [{ path: 'credit-notes', element: <CreditNotesPage /> }],
          },
          {
            element: <RequireAccess permission="debit_notes.view" module="debit_notes" />,
            children: [{ path: 'debit-notes', element: <DebitNotesPage /> }],
          },
          {
            element: <RequireAccess permission="sales_returns.view" module="sales_returns" />,
            children: [{ path: 'sales-returns', element: <SalesReturnsPage /> }],
          },
          {
            element: <RequireAccess permission="payments.view" module="payments" />,
            children: [
              { path: 'payments', element: <PaymentsListPage /> },
              { path: 'payments/:id', element: <PaymentDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="payments.view" module="receivables" />,
            children: [
              { path: 'receivables/aging', element: <ReceivablesAgingPage /> },
              {
                path: 'receivables/customers/:customerId/statement',
                element: <CustomerStatementPage />,
              },
            ],
          },
          {
            element: <RequireAccess permission="payments.view" module="bank_accounts" />,
            children: [
              { path: 'bank-settings', element: <BankSettingsPage /> },
            ],
          },
          // ── Purchasing ───────────────────────────────────────
          {
            element: <RequireAccess permission="suppliers.view" module="purchases" />,
            children: [{ path: 'suppliers', element: <SuppliersListPage /> }],
          },
          {
            element: <RequireAccess permission="purchase_orders.view" module="purchases" />,
            children: [
              { path: 'purchase-orders', element: <PurchaseOrdersListPage /> },
              { path: 'purchase-orders/:id', element: <PurchaseOrderDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="goods_receipts.view" module="purchases" />,
            children: [{ path: 'goods-receipts', element: <GoodsReceiptsPage /> }],
          },

          // ── Field Operations ─────────────────────────────────
          {
            element: <RequireAccess permission="field_force.view" module="field_force" />,
            children: [
              { path: 'field-force', element: <SalesRepsListPage /> },
              { path: 'field-force/:id', element: <SalesRepDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="field_force.view" module="beats" />,
            children: [
              { path: 'territories', element: <TerritoriesPage /> },
              { path: 'territories/map', element: <TerritoryMapPage /> },
              { path: 'territories/coverage', element: <TerritoryCoveragePage /> },
            ],
          },
          {
            element: <RequireAccess permission="field_force.view" module="beats" />,
            children: [
              { path: 'beats', element: <BeatsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="visits.view" module="visits" />,
            children: [
              { path: 'visits', element: <VisitsListPage /> },
              { path: 'visits/:id', element: <VisitDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="visits.view" module="samples_management" />,
            children: [{ path: 'samples', element: <SamplesPage /> }],
          },
          {
            element: <RequireAccess permission="visit_cooldown_rules.view" module="visits" />,
            children: [{ path: 'visit-cooldown-rules', element: <CooldownRulesPage /> }],
          },
          {
            element: <RequireAccess permission="visit_overrides.view" module="visits" />,
            children: [{ path: 'visit-overrides', element: <VisitOverridesPage /> }],
          },
          // ── Field Service ────────────────────────────────────
          {
            element: <RequireAccess permission="service_tickets.view" module="service_tickets" />,
            children: [
              { path: 'service-tickets', element: <ServiceTicketsListPage /> },
              { path: 'service-tickets/:id', element: <ServiceTicketDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="technicians.view" module="technicians" />,
            children: [{ path: 'technicians', element: <TechniciansListPage /> }],
          },
          // ── Verticals ────────────────────────────────────────
          {
            element: <RequireAccess permission="pharma.view" module="doctor_calls" />,
            children: [{ path: 'verticals/pharma', element: <PharmaPage /> }],
          },
          {
            element: <RequireAccess permission="pharma.batches.view" module="batches" />,
            children: [{ path: 'verticals/pharma/batches', element: <PharmaBatchesPage /> }],
          },
          {
            element: <RequireAccess permission="cold_chain.view" module="cold_chain" />,
            children: [{ path: 'verticals/cold-chain', element: <ColdChainPage /> }],
          },
          {
            element: <RequireAccess permission="agriculture.view" module="agriculture" />,
            children: [{ path: 'verticals/agriculture', element: <AgriculturePage /> }],
          },
          {
            element: <RequireAccess permission="horeca.view" module="horeca_management" />,
            children: [{ path: 'verticals/horeca', element: <HorecaPage /> }],
          },
          {
            element: <RequireAccess permission="construction.view" module="construction" />,
            children: [{ path: 'verticals/construction', element: <ConstructionPage /> }],
          },

          // ── Batch 60 — Sub-feature pages ─────────────────────
          {
            element: <RequireAccess permission="pharma.view" module="drap_compliance" />,
            children: [
              { path: 'verticals/pharma/drap-reports', element: <PharmaDrapReportsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="pharma.view" module="rx_audit" />,
            children: [
              { path: 'verticals/pharma/rx-audit', element: <PharmaRxAuditPage /> },
            ],
          },
          {
            element: <RequireAccess permission="pharma.view" module="prescriptions" />,
            children: [
              { path: 'verticals/pharma/prescriptions', element: <PharmaPrescriptionsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="pharma.view" module="brand_performance" />,
            children: [
              { path: 'verticals/pharma/brand-performance', element: <PharmaBrandPerformancePage /> },
            ],
          },
          {
            element: <RequireAccess permission="cold_chain.view" module="cold_chain_custody" />,
            children: [
              { path: 'verticals/cold-chain/custody', element: <ColdChainCustodyPage /> },
            ],
          },
          {
            element: <RequireAccess permission="agriculture.view" module="ag_advisory" />,
            children: [
              { path: 'verticals/agriculture/tools', element: <AgricultureToolsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="horeca.view" module="horeca_recipes" />,
            children: [
              { path: 'verticals/horeca/tools', element: <HorecaToolsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="auto_parts.view" module="auto_parts" />,
            children: [
              { path: 'verticals/auto-parts', element: <AutoPartsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="construction.view" module="boq_management" />,
            children: [
              { path: 'verticals/construction/tools', element: <ConstructionToolsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="modern_trade.view" module="modern_trade" />,
            children: [
              { path: 'verticals/modern-trade', element: <ModernTradePage /> },
            ],
          },
          {
            element: <RequireAccess permission="reports.view" module="sales_reports" />,
            children: [
              { path: 'reports/by-category', element: <ReportsByCategoryPage /> },
            ],
          },
          {
            element: <RequireAccess permission="fbr.view" module="fbr_compliance" />,
            children: [
              { path: 'fbr/dashboard', element: <FbrDashboardPage /> },
            ],
          },
          {
            element: <RequireAccess permission="subscription.manage" module="subscription_billing" />,
            children: [
              { path: 'subscription/actions', element: <SubscriptionActionsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="audit.revert" module="audit_revert" />,
            children: [
              { path: 'audit/deep-tools', element: <AuditDeepToolsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="accounting_exports.view" module="accounting_sync" />,
            children: [
              { path: 'accounting-exports/manage', element: <AccountingExportsPageExtended /> },
            ],
          },
          {
            element: <RequireAccess permission="whatsapp.send" module="sms" />,
            children: [
              { path: 'comms/extra', element: <CommsExtraPage /> },
            ],
          },
          {
            element: <RequireAccess permission="plugins.view" module="custom_plugins" />,
            children: [
              { path: 'plugins/advanced', element: <PluginsAdvancedPage /> },
            ],
          },
          {
            element: <RequireAccess permission="ps_requests.view" module="ps_hour_tracking" />,
            children: [
              { path: 'ps/hours-invoices', element: <PsHoursInvoicesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="gps.view" module="geofencing" />,
            children: [
              { path: 'gps/geofences', element: <GeofencesPage /> },
            ],
          },

          // ── Batch 61 — Gap-coverage pages ────────────────────
          {
            element: <RequireAccess permission="backups.view" />,
            children: [
              { path: 'admin/backups', element: <BackupAdminPage /> },
            ],
          },
          // Profile is available to every authenticated user — no permission gate
          { path: 'profile', element: <MyProfilePage /> },
          {
            element: <RequireAccess permission="reports.view" module="reports" />,
            children: [
              { path: 'admin/dashboards', element: <CustomDashboardsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="document_templates.manage" module="document_templates" />,
            children: [
              { path: 'admin/invoice-templates', element: <InvoiceTemplatesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="settings.view" />,
            children: [
              // admin/modules route removed — module enablement is superadmin-only
            ],
          },

          // ── Reports ──────────────────────────────────────────
          {
            element: <RequireAccess permission="reports.view" module="reports" />,
            children: [{ path: 'reports', element: <ReportsPage /> }],
          },
          // ── Admin ────────────────────────────────────────────
          {
            element: <RequireAccess permission="settings.view" module="audit_query" />,
            children: [{ path: 'audit-logs', element: <AuditLogsPage /> }],
          },
          {
            element: <RequireAccess permission="audit.view" module="notifications" />,
            children: [{ path: 'notification-failures', element: <NotificationFailuresPage /> }],
          },

          {
            element: <RequireAccess permission="subscription.view" />,
            children: [{ path: 'subscription', element: <SubscriptionPage /> }],
          },
          {
            element: <RequireAccess permission="whatsapp.templates.view" module="whatsapp_business" />,
            children: [{ path: 'whatsapp', element: <WhatsAppPage /> }],
          },
          {
            element: <RequireAccess permission="webhooks.view" module="webhooks" />,
            children: [{ path: 'webhooks', element: <WebhooksPage /> }],
          },
          {
            element: <RequireAccess permission="fbr.view" module="fbr" />,
            children: [{ path: 'fbr', element: <FbrPage /> }],
          },
          {
            element: <RequireAccess permission="plugins.view" module="plugins" />,
            children: [{ path: 'plugins', element: <PluginsPage /> }],
          },
          {
            element: <RequireAccess permission="ps_requests.view" module="ps_engagements" />,
            children: [{ path: 'professional-services', element: <ProfessionalServicesPage /> }],
          },
          // ── Order depth (P49-51) ────────────────────────────
          {
            element: <RequireAccess permission="orders.view" module="orders" />,
            children: [
              { path: 'recurring-orders', element: <RecurringOrdersListPage /> },
              { path: 'recurring-orders/:id', element: <RecurringOrderDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="refunds.view" module="refunds" />,
            children: [{ path: 'refunds', element: <RefundsListPage /> }],
          },
          {
            element: <RequireAccess permission="purchase_returns.view" module="purchases" />,
            children: [{ path: 'purchase-returns', element: <PurchaseReturnsListPage /> }],
          },
          {
            element: <RequireAccess permission="quotes.view" module="service_quotes" />,
            children: [{ path: 'service-quotes', element: <ServiceQuotesListPage /> }],
          },
          // ── Rep ops + catalog depth (P52-53) ─────────────────
          {
            element: <RequireAccess permission="journey_plans.view" module="field_force" />,
            children: [{ path: 'journey-plans', element: <JourneyPlansListPage /> }],
          },
          {
            element: <RequireAccess permission="attendance.view" module="field_force" />,
            children: [{ path: 'attendance', element: <AttendancePage /> }],
          },
          {
            element: <RequireAccess permission="attendance.view" module="gps_tracking" />,
            children: [{ path: 'live-tracking', element: <LiveTrackingPage /> }],
          },
          {
            element: <RequireAccess permission="delivery_runs.view" module="dispatch_board" />,
            children: [{ path: 'dispatch', element: <DispatchBoardPage /> }],
          },
          {
            element: <RequireAccess permission="delivery_runs.view" module="delivery_runs" />,
            children: [
              { path: 'delivery-runs', element: <DeliveryRunsListPage /> },
              { path: 'delivery-runs/:id', element: <DeliveryRunDetailPage /> },
              { path: 'delivery-runs/:id/replay', element: <RunReplayPage /> },
            ],
          },
          {
            element: <RequireAccess permission="pick_pack.view" module="pick_pack" />,
            children: [
              { path: 'pick-lists', element: <PickListsPage /> },
              { path: 'pick-lists/:id', element: <PickListDetailPage /> },
            ],
          },
          {
            element: <RequireAccess permission="drivers.view" module="drivers" />,
            children: [{ path: 'drivers', element: <DriversListPage /> }],
          },
          {
            element: <RequireAccess permission="drivers.view" module="delivery_runs" />,
            children: [
              { path: 'driver-pay', element: <DriverPayPage /> },
              { path: 'driver-operations', element: <DriverCodShiftsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="vehicles.view" module="vehicles" />,
            children: [{ path: 'vehicles', element: <VehiclesListPage /> }],
          },
          {
            element: <RequireAccess permission="pallets.view" module="pallets" />,
            children: [{ path: 'pallets', element: <PalletsPage /> }],
          },
          {
            element: <RequireAccess permission="label_template.view" module="shipment_label_templates" />,
            children: [{ path: 'shipment-labels', element: <ShipmentLabelsPage /> }],
          },
          {
            element: <RequireAccess permission="cod.view" module="cod_handovers" />,
            children: [
              { path: 'finance/cod-handovers', element: <FinanceCodHandoversPage /> },
            ],
          },
          {
            element: <RequireAccess permission="cod.view" module="bank_statement_imports" />,
            children: [
              { path: 'finance/bank-imports', element: <BankImportsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="zatca.view" module="zatca_config" />,
            children: [
              { path: 'tax/zatca/config', element: <ZatcaConfigPage /> },
            ],
          },
          {
            element: <RequireAccess permission="zatca.view" module="zatca_einvoices" />,
            children: [
              { path: 'tax/zatca/einvoices', element: <ZatcaEinvoicesPage /> },
            ],
          },
          {
            element: <RequireAccess permission="zatca.view" module="zatca_webhook_events" />,
            children: [
              { path: 'tax/zatca/webhook-events', element: <ZatcaWebhookEventsPage /> },
            ],
          },
          {
            element: <RequireAccess permission="ecommerce.view" module="ecommerce_stores" />,
            children: [{ path: 'ecommerce/stores', element: <EcommerceStoresPage /> }],
          },
          {
            children: [{ path: 'notifications-inbox', element: <NotificationsInboxPage /> }],
          },
          {
            element: <RequireAccess permission="customers.view" module="customer_segmentation" />,
            children: [{ path: 'customer-segments', element: <CustomerSegmentsPage /> }],
          },
          {
            element: <RequireAccess permission="products.view" module="products" />,
            children: [
              { path: 'brands', element: <BrandsPage /> },
              { path: 'units', element: <UnitsOfMeasurePage /> },
            ],
          },
          {
            element: <RequireAccess permission="pricing.view" module="pricing_rules" />,
            children: [
              { path: 'pricing-rules', element: <PricingRulesListPage /> },
              { path: 'schemes', element: <SchemesListPage /> },
            ],
          },
          {
            element: <RequireAccess permission="users.create" />,
            children: [{ path: 'invitations', element: <InvitationsPage /> }],
          },
          // ── Field service depth (P54-55) ─────────────────────
          {
            element: <RequireAccess permission="skills.view" module="service_tickets" />,
            children: [{ path: 'skills', element: <SkillsPage /> }],
          },
          {
            element: <RequireAccess permission="sla_policies.view" module="service_tickets" />,
            children: [{ path: 'sla-policies', element: <SlaPoliciesPage /> }],
          },
          {
            element: <RequireAccess permission="customer_assets.view" module="service_tickets" />,
            children: [{ path: 'customer-assets', element: <CustomerAssetsPage /> }],
          },
          {
            element: <RequireAccess permission="checklist_templates.view" module="service_tickets" />,
            children: [{ path: 'checklist-templates', element: <ChecklistTemplatesPage /> }],
          },
          {
            element: <RequireAccess permission="kb_articles.view" module="service_tickets" />,
            children: [{ path: 'knowledge-base', element: <KnowledgeBasePage /> }],
          },
          {
            element: <RequireAccess permission="service_tickets.view" module="service_tickets" />,
            children: [{ path: 'field-service/analytics', element: <FieldServiceAnalyticsPage /> }],
          },
          // ── Power admin (P58-59) ───────────────────────────
          {
            element: <RequireAccess permission="custom_fields.view" module="custom_fields" />,
            children: [{ path: 'admin/custom-fields', element: <CustomFieldsPage /> }],
          },
          {
            element: <RequireAccess permission="workflows.view" module="workflows" />,
            children: [{ path: 'admin/workflows', element: <WorkflowsPage /> }],
          },
          {
            element: <RequireAccess permission="custom_reports.view" module="custom_reports" />,
            children: [{ path: 'admin/custom-reports', element: <CustomReportsPage /> }],
          },
          {
            element: <RequireAccess permission="forms.view" module="custom_forms" />,
            children: [{ path: 'admin/forms', element: <FormsPage /> }],
          },
          {
            element: <RequireAccess permission="document_templates.view" module="document_templates" />,
            children: [{ path: 'admin/document-templates', element: <DocumentTemplatesPage /> }],
          },
          {
            element: <RequireAccess permission="notification_rules.view" module="notification_rules" />,
            children: [{ path: 'admin/notification-rules', element: <NotificationRulesPage /> }],
          },
          {
            element: <RequireAccess permission="alert_rules.view" module="notification_rules" />,
            children: [{ path: 'admin/alert-rules', element: <AlertRulesPage /> }],
          },
          {
            element: <RequireAccess permission="contracts.view" module="service_contracts" />,
            children: [{ path: 'service-contracts', element: <ContractsPage /> }],
          },
          {
            element: <RequireAccess permission="api_tokens.view" module="public_api" />,
            children: [{ path: 'admin/api-tokens', element: <ApiTokensPage /> }],
          },
          {
            element: <RequireAccess permission="roles.view" module="custom_roles" />,
            children: [{ path: 'admin/custom-roles', element: <CustomRolesPage /> }],
          },
          {
            element: <RequireAccess permission="subscription.view" module="quotas" />,
            children: [{ path: 'admin/quotas', element: <QuotasPage /> }],
          },
          {
            element: <RequireAccess permission="whatsapp.templates.view" module="whatsapp_business" />,
            children: [{ path: 'admin/whatsapp-templates', element: <WhatsAppTemplatesPage /> }],
          },
          {
            element: <RequireAccess permission="fbr.view" module="fbr" />,
            children: [{ path: 'admin/fbr-settings', element: <FbrSettingsPage /> }],
          },
          {
            element: <RequireAccess permission="audit.view" module="audit_query" />,
            children: [{ path: 'admin/customization-audit', element: <AuditCustomizationPage /> }],
          },
          // ── Integrations (P60) ──────────────────────────────
          {
            element: <RequireAccess permission="edi.view" module="erp_integration" />,
            children: [{ path: 'edi', element: <EdiPage /> }],
          },
          {
            element: <RequireAccess permission="imports.view" />,
            children: [{ path: 'imports', element: <ImportsPage /> }],
          },
          {
            element: <RequireAccess permission="exports.view" />,
            children: [{ path: 'exports', element: <ExportsPage /> }],
          },
          {
            element: <RequireAccess permission="accounting_exports.view" />,
            children: [{ path: 'accounting-exports', element: <AccountingExportsPage /> }],
          },
          {
            element: <RequireAccess permission="reports.view" />,
            children: [{ path: 'scheduled-reports', element: <ScheduledReportsPage /> }],
          },
          {
            element: <RequireAccess permission="sync.view" />,
            children: [{ path: 'sync-admin', element: <SyncAdminPage /> }],
          },
          {
            element: <RequireAccess permission="payment_gateways.view" moduleAny={['gateway_jazzcash', 'gateway_easypaisa', 'gateway_stripe']} />,
            children: [{ path: 'payment-gateways', element: <PaymentGatewaysPage /> }],
          },
          {
            element: <RequireAccess permission="marketplace.browse" module="marketplace" />,
            children: [{ path: 'marketplace', element: <MarketplaceCatalogPage /> }],
          },
          {
            element: <RequireAccess permission="marketplace.orders.view" module="marketplace" />,
            children: [{ path: 'marketplace/orders', element: <MarketplaceOrdersPage /> }],
          },
          {
            element: <RequireAccess permission="marketplace.recurring.manage" module="marketplace" />,
            children: [{ path: 'marketplace/auto-reorder', element: <MarketplaceRecurringPage /> }],
          },
          {
            element: <RequireAccess permission="marketplace.listings.manage" module="marketplace" />,
            children: [{ path: 'marketplace/listings', element: <SellerListingsPage /> }],
          },
          // ── Customer Portal session admin (P61) ─────────────
          {
            element: <RequireAccess permission="customers.view" module="customers" />,
            children: [{ path: 'admin/customer-portal-sessions', element: <PortalSessionsPage /> }],
          },
          // ── Polish (P62) ────────────────────────────────────
          { path: 'notifications', element: <NotificationsPage /> },
          {
            element: <RequireAccess permission="settings.view" />,
            children: [
              { path: 'admin/storage', element: <StorageBrowserPage /> },
              { path: 'audit-logs/:id', element: <AuditLogDetailPage /> },
            ],
          },
          {
            element: (
              <RequireAccess permission="visit_cooldown_rules.view" module="visit_cooldown_rules" />
            ),
            children: [
              {
                path: 'settings/visit-cooldown',
                element: <ComingSoonPage title="Visit Cooldown Rules" batch="Batch P19" />,
              },
            ],
          },
          {
            element: <RequireAccess permission="visit_overrides.view" module="visit_overrides" />,
            children: [
              {
                path: 'field-ops/overrides',
                element: <ComingSoonPage title="Override Requests" batch="Batch P19" />,
              },
            ],
          },

          // ── Administration ───────────────────────────────────
          {
            element: <RequireAccess permission="users.view" />,
            children: [{ path: 'users', element: <UsersListPage /> }],
          },
          {
            element: <RequireAccess permission="roles.view" />,
            children: [{ path: 'roles', element: <RolesPage /> }],
          },
          {
            element: <RequireAccess permission="company_settings.view" />,
            children: [{ path: 'settings', element: <SettingsPage /> }],
          },

          // Catch-all inside the shell
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
  // ── Public customer portal (P61) — token-auth, no login ─
  {
    path: '/portal/:token',
    element: <PortalShell />,
    children: [
      { index: true, element: <PortalOverviewPage /> },
      { path: 'tickets', element: <PortalTicketsPage /> },
      { path: 'tickets/:ticketId', element: <PortalTicketDetailPage /> },
      { path: 'assets', element: <PortalAssetsPage /> },
      { path: 'assets/:assetId', element: <PortalAssetDetailPage /> },
      { path: 'kb', element: <PortalKbPage /> },
    ],
  },
  // Top-level catch-all
  { path: '*', element: <Navigate to="/" replace /> },
])
